import { useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useConversas } from '@/conversas/ConversasProvider';
import { transferirConversa } from '@/services/conversasService';
import { nomeDoContato } from '@/conversas/contato';
import { PAPEIS_POR_ESPACO } from '@/lib/constants';


const MENSAGENS = {
  'functions/failed-precondition': null, // a Function já explica o porquê
  'functions/permission-denied': 'Seu papel não pode transferir esta conversa.',
};

/**
 * Transferência entre atendentes DO MESMO SETOR (PLANO 5.3).
 *
 * ★★★ C1 (WS parte 2) — O SELETOR DE SETOR SAIU.
 *
 * ⚠⚠ Decisão do dono (03/out/2026): conversa não transfere entre Vendas e Recepção, porque
 * **o espaço é o do NÚMERO** que atende. ★ O seletor "Espaço de destino" que existia aqui era
 * a porta pela qual as **9 conversas divergentes** de produção nasceram: a tela gravava um
 * espaço novo e o `numeroId` ficava como estava, então a conversa sumia da caixa de quem a
 * atende e aparecia na de quem não a atende.
 *
 * ★★ A `transferirConversa` agora **recusa** `paraEspaco` diferente — a tela não é a guarda,
 * é a cortesia de não oferecer o que o servidor vai negar.
 */
export default function DialogoTransferencia({ aberto, aoFechar }) {
  const { clinicaId, conversa, equipe } = useConversas();
  /**
   * ★★★ O setor é LIDO da conversa, nunca escolhido — e é calculado a cada render, sem
   * `useState`. ⚠⚠ Com estado, abrir o diálogo antes de a conversa chegar congelaria
   * `'vendas'` e a transferência iria para o setor errado. **É a regra da WT: valor derivado
   * de dado assíncrono não mora em `useState`.**
   */
  const paraEspaco = conversa?.espaco ?? null;
  const [paraUsuarioId, setParaUsuarioId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);

  // Só oferece quem realmente atende o espaço de destino — a Function recusa o
  // resto, e oferecer para depois recusar seria um convite ao erro.
  const candidatos = equipe.filter(
    (u) => u.ativo !== false && PAPEIS_POR_ESPACO[paraEspaco]?.includes(u.papel),
  );

  function fechar() {
    setMotivo('');
    setParaUsuarioId('');
    setErro(null);
    aoFechar();
  }

  async function transferir(evento) {
    evento.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await transferirConversa({
        clinicaId,
        conversaId: conversa.id,
        paraEspaco,
        paraUsuarioId: paraUsuarioId || null,
        motivo,
      });
      fechar();
    } catch (e) {
      setErro(MENSAGENS[e.code] ?? e.message ?? 'Não foi possível transferir.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialogo titulo="Transferir conversa" aberto={aberto} aoFechar={fechar}>
      <form className="formulario" onSubmit={transferir}>
        <p className="dialogo-contexto">
          {conversa ? nomeDoContato(conversa) : ''} · atualmente em{' '}
          <strong>{conversa?.espaco === 'recepcao' ? 'Recepção' : 'Vendas'}</strong>
          {conversa?.status === 'resolvida' ? ', encerrada' : conversa?.responsavelNome ? ` com ${conversa.responsavelNome}` : ', sem responsável'}
        </p>

        <div className="campo">
          <label htmlFor="dt-usuario">Atendente</label>
          <select
            id="dt-usuario"
            value={paraUsuarioId}
            onChange={(e) => setParaUsuarioId(e.target.value)}
          >
            <option value="">Deixar na fila de aguardando atendimento</option>
            {candidatos.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
          {candidatos.length === 0 && (
            <span className="indicador-nota">
              Ninguém cadastrado para este setor — a conversa vai para a fila.
            </span>
          )}
        </div>

        <div className="campo">
          <label htmlFor="dt-motivo">Motivo (opcional)</label>
          <input
            id="dt-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="ex.: paciente já em tratamento"
          />
        </div>

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={fechar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={enviando}>
            {enviando ? 'Transferindo…' : 'Transferir'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
